import React, { useEffect, useState } from "react";
import { Badge, Icon, Modal } from "~/components-shared";
import SetupGuideModal from "~/components/SetupGuideModal";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum, StatusStateEnum } from "~/types/enums";
import integrationSelectors from "~/store/integrations/selectors";
import topicsActionCreators from "~/store/topics/actions";
import topicSelectors from "~/store/topics/selectors";
import insightsActionCreators from "~/store/insights/actions";
import insightsSelectors from "~/store/insights/selectors";
import invitationSelectors from "~/store/invitations/selectors";
import invitationsActionCreators from "~/store/invitations/actions";
import api from "~/lib/api";
import useAbacExt from "~/hooks/abac";
import { useTranslation } from "react-i18next";
import { useInvitationsWrite } from "~/hooks/common";
import authSelectors from "~/store/auth/selectors";
import Button from "../../components-shared/Button";
const StyledBadge = styled(Badge)`
  position: absolute;
  top: 50%;
  right: 1em;
  transform: translateY(-50%);
  min-width: 2em;
  display: inline-block;
  text-align: center;
  color: #ffffff;
`;
const StyledButton = styled(Button)`
  color: #ffffff;
  background-color: #6c5dd3;
`;
const SetupGuide = () => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    userHas
  } = useAbacExt();
  const setupGuide = {};
  const [statusOpen, setStatusOpen] = useState(false);
  const invitationsWrite = useInvitationsWrite();
  const [slackChannels, setSlackChannels] = useState([]);
  const user = useSelector(authSelectors.getUser);
  const integrations = useSelector(integrationSelectors.getIntegrations);
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const jiraIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.JIRA));
  const confluenceIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.CONFLUENCE));
  const linearIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.LINEAR));
  const asanaIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.ASANA));
  const githubIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.GITHUB));
  const productboardIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.PRODUCTBOARD));
  const mondayIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.MONDAY));
  const topicStatus = useSelector(topicSelectors.getStatus);
  const topics = useSelector(topicSelectors.getTopics);
  const topicSubscribeStatus = useSelector(topicSelectors.getSubscribeStatus);
  const topicSubscribed = useSelector(topicSelectors.getSubscribed);
  const insightsConfigsStatus = useSelector(insightsSelectors.getConfigsStatus);
  const insightsConfigs = useSelector(insightsSelectors.getConfigs);
  const invitationsStatus = useSelector(invitationSelectors.getStatus);
  const invitations = useSelector(invitationSelectors.getInvitations);
  useEffect(() => {
    if (slackIntegration) {
      api.fetchSlackChannels().then(response => {
        setSlackChannels(response);
      });
    }
  }, [slackIntegration]);
  useEffect(() => {
    if (userHas([AuthoritiesEnum.TOPICS_READ, AuthoritiesEnum.TOPICS_WRITE]) && topicStatus === StatusStateEnum.IDLE) {
      dispatch(topicsActionCreators.fetchStarted());
    }
  }, [userHas, dispatch, topicStatus]);
  useEffect(() => {
    if (userHas([AuthoritiesEnum.TOPICS_READ, AuthoritiesEnum.TOPICS_WRITE]) && topicSubscribeStatus === StatusStateEnum.IDLE) {
      dispatch(topicsActionCreators.fetchSubscriptionStarted());
    }
  }, [userHas, dispatch, topicSubscribeStatus]);
  useEffect(() => {
    if (userHas([AuthoritiesEnum.INSIGHTS_READ, AuthoritiesEnum.INSIGHTS_WRITE]) && insightsConfigsStatus === StatusStateEnum.IDLE) {
      dispatch(insightsActionCreators.fetchConfigsStarted());
    }
  }, [userHas, dispatch, insightsConfigsStatus]);
  useEffect(() => {
    if (userHas([AuthoritiesEnum.INVITATIONS_READ]) && invitationsWrite && invitationsStatus === StatusStateEnum.IDLE) {
      dispatch(invitationsActionCreators.fetchStarted());
    }
  }, [userHas, dispatch, invitationsStatus]);
  if (userHas([AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.INTEGRATIONS_WRITE])) {
    setupGuide.connectedIntegrations = !!integrations.length;
    integrations.map(async integration => {
      switch (integration.type) {
        case IntegrationTypeEnum.SLACK:
          {
            setupGuide.slackProfileConnected = !!user.metadata.slackUserId;
            setupGuide.addedSlackChannels = !!slackChannels.filter(channel => channel.isMember).length;
            break;
          }
        case IntegrationTypeEnum.JIRA:
          setupGuide.addedJiraProjects = !!jiraIntegration?.settings?.projects?.length;
          break;
        case IntegrationTypeEnum.CONFLUENCE:
          setupGuide.addedConfluenceSpaces = !!confluenceIntegration?.settings?.spaces?.length;
          break;
        case IntegrationTypeEnum.LINEAR:
          setupGuide.addedLinearTeams = !!linearIntegration?.settings?.teams?.length;
          break;
        case IntegrationTypeEnum.ASANA:
          setupGuide.addedAsanaProjects = !!asanaIntegration?.settings?.projects?.length;
          break;
        case IntegrationTypeEnum.GITHUB:
          setupGuide.addedGithubRepositories = !!githubIntegration?.settings?.repositories?.length;
          break;
        case IntegrationTypeEnum.PRODUCTBOARD:
          setupGuide.addedProductboardProducts = !!productboardIntegration?.settings?.products?.length;
          break;
        case IntegrationTypeEnum.MONDAY:
          setupGuide.addedMondayBoards = !!mondayIntegration?.settings?.boards?.length;
          break;
        default:
          break;
      }
    });
  }
  if (userHas([AuthoritiesEnum.TOPICS_READ, AuthoritiesEnum.TOPICS_WRITE])) {
    setupGuide.topicCreated = !!topics.length;
    if (slackIntegration) {
      setupGuide.topicReportSubscribed = topicSubscribed;
    }
  }
  if (userHas([AuthoritiesEnum.INSIGHTS_READ, AuthoritiesEnum.INSIGHTS_WRITE])) {
    setupGuide.insightsReportCreated = !!insightsConfigs.length;
  }
  if (userHas([AuthoritiesEnum.INVITATIONS_READ]) && invitationsWrite) {
    setupGuide.membersInvited = !!invitations.length;
  }
  const required = Object.keys(setupGuide).filter(item => item === "membersInvited" ? false : !setupGuide[item]).length;
  if (required === 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledButton, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "explore",
      scale: 5,
      s: {
        mr: 2
      }
    }),
    textAlign: "left",
    color: "info",
    onClick: () => setStatusOpen(true),
    outline: false,
    fullWidth: true
  }, t("SetupGuide.setupGuide"), " ", /*#__PURE__*/React.createElement(StyledBadge, {
    s: {
      px: 0
    },
    color: "translucent",
    fontSize: 3.5,
    weight: "semiBold"
  }, required > 0 ? required : /*#__PURE__*/React.createElement(Icon, {
    src: "tick",
    s: {
      mt: -0.5
    }
  }))), /*#__PURE__*/React.createElement(Modal, {
    open: statusOpen
  }, /*#__PURE__*/React.createElement(SetupGuideModal, {
    setupGuide: setupGuide,
    onClose: () => setStatusOpen(false)
  })));
};
export default SetupGuide;